import { useEffect, useRef, useState } from "react";
import { Loader2, MessageCircle, Send, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { API } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

// Free AI live chat — "Aisha", the clinic's virtual receptionist. Zero cost
// (free AI cascade server-side), 30 msgs/day per visitor, captures phone leads
// straight into the WhatsApp bot.
export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const sessionRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    let sid = localStorage.getItem("rsc_chat_session");
    if (!sid) {
      sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("rsc_chat_session", sid);
    }
    sessionRef.current = sid;
    fetch(`${API}/chat/${sid}`)
      .then((r) => r.json())
      .then((d) => setMessages(d.messages || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, typing]);

  // staff can reply from the backoffice — poll for their messages while open
  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(() => {
      fetch(`${API}/chat/${sessionRef.current}`)
        .then((r) => r.json())
        .then((d) => {
          const msgs = d.messages || [];
          setMessages((cur) => (msgs.length > cur.length ? msgs : cur));
        })
        .catch(() => {});
    }, 12000);
    return () => clearInterval(t);
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || typing) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text, at: new Date().toISOString() }]);
    setTyping(true);
    try {
      const r = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: text }),
      });
      const d = await r.json();
      setMessages((m) => [...m, { role: "assistant", text: d.reply, at: new Date().toISOString() }]);
      trackEvent("ai_chat_message");
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "I'm sorry, I lost my connection for a moment — could you say that again?", at: new Date().toISOString() }]);
    } finally {
      setTyping(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        onClick={() => setOpen((v) => !v)}
        aria-label="Chat with our assistant"
        data-testid="ai-chat-button"
        className="fixed bottom-[92px] right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#1A1615] text-[#D4AF37] shadow-[0_10px_30px_rgba(26,22,21,0.35)] transition-transform duration-500 hover:scale-110"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-[158px] right-6 z-40 flex h-[480px] w-[min(92vw,380px)] flex-col overflow-hidden rounded-2xl border border-[#E8E2D9] bg-[#FAF8F5] shadow-2xl"
            data-testid="ai-chat-panel"
          >
            <div className="bg-[#1A1615] px-5 py-4">
              <p className="font-serif text-base font-semibold text-white">Aisha · Royal Skin Concierge</p>
              <p className="text-[10px] font-light tracking-wide text-[#D4AF37]/80">Typically replies in seconds — ask anything, or book a consultation</p>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="ai-chat-messages">
              {messages.length === 0 && (
                <div className="rounded-2xl rounded-tl-sm bg-white px-4 py-3 text-[13px] font-light leading-relaxed text-[#1A1615] shadow-sm">
                  Hello, welcome to Royal Skin Cosmetic Clinic. I'm Aisha — whether it's a treatment question or booking a consultation, I'm here to help.
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-4 py-3 text-[13px] font-light leading-relaxed shadow-sm ${m.role === "user" ? "rounded-2xl rounded-br-sm bg-[#1A1615] text-[#FAF8F5]" : "rounded-2xl rounded-tl-sm bg-white text-[#1A1615]"}`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {typing && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-1.5 rounded-2xl rounded-tl-sm bg-white px-4 py-3 shadow-sm" data-testid="ai-chat-typing">
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#B38B2D]" style={{ animationDelay: "0ms" }} />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#B38B2D]" style={{ animationDelay: "150ms" }} />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#B38B2D]" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>
            <div className="flex items-center gap-2 border-t border-[#EFE7DF] bg-white p-3">
              <input
                className="flex-1 rounded-full border border-[#E8E2D9] bg-[#FAF8F5] px-4 py-2.5 text-[13px] font-light text-[#1A1615] outline-none placeholder:text-[#B8AEA5] focus:border-[#C5A059]"
                placeholder="Type your message…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                data-testid="ai-chat-input"
              />
              <button
                onClick={send}
                disabled={typing || !input.trim()}
                data-testid="ai-chat-send"
                aria-label="Send message"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1A1615] text-[#D4AF37] transition-opacity disabled:opacity-40"
              >
                {typing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
